import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { detectPrfSupport, supportsWalletExport, type PrfSupportResult } from '@cef-ai/wallet-identity';
import { useWallet } from '~/wallet';
import { AppPage } from '~/ui/AppPage';
import { Card, Chip, CopyValue, Loading } from '~/ui/primitives';
import { ExportAccount } from './ExportAccount';
import { EmailNudge } from './EmailNudge';

function DiagRow({ label, ok }: { label: string; ok: boolean }) {
  return (
    <div
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--space-2) 0' }}
    >
      <span style={{ font: '400 14px/1.4 var(--font-sans)', color: 'var(--text-secondary)' }}>{label}</span>
      <Chip tone={ok ? 'success' : 'neutral'}>{ok ? 'Yes' : 'No'}</Chip>
    </div>
  );
}

export const Settings = observer(() => {
  const { identity } = useWallet();
  const [diag, setDiag] = useState<PrfSupportResult | null>(null);

  useEffect(() => {
    detectPrfSupport()
      .then(setDiag)
      .catch(() => setDiag(null));
  }, []);

  if (!identity.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AppPage title="Settings">
      {/* Never a gate: it renders above the real content, which stays fully
          usable whether or not it is shown or dismissed. See `EmailNudge`. */}
      <EmailNudge account={identity.account} />
      <Card>
        <h2 style={{ margin: '0 0 var(--space-4)', font: '600 16px/1.2 var(--font-sans)' }}>Credential</h2>
        <CopyValue label="Credential ID" value={identity.credentialId ?? '—'} truncate={false} />
      </Card>
      <Card>
        <h2 style={{ margin: '0 0 var(--space-3)', font: '600 16px/1.2 var(--font-sans)' }}>Diagnostics</h2>
        {diag ? (
          <div>
            <DiagRow label="WebAuthn available" ok={diag.webAuthn} />
            <DiagRow label="Platform authenticator" ok={diag.platformAuthenticator} />
            <DiagRow label="PRF potentially supported" ok={diag.prfPotentiallySupported} />
          </div>
        ) : (
          <Loading />
        )}
      </Card>
      {supportsWalletExport(identity) && <ExportAccount identity={identity} />}
    </AppPage>
  );
});
